/**
 * Active-brand web service (Faz-5 PR-5, roadmap #441, umbrella #421).
 *
 * Thin HTTP client over `GET /api/entity/active-brand[/owned]`. The resolver +
 * IDOR guard live server-side (owner_id = userId); this layer only fetches.
 *
 * `getActiveBrand` returns `null` on 204 (the caller owns no themeable brand) so
 * the store/cascade can fall back to the PR-3 default tokens. A 403 (requesting a
 * non-owned brand) surfaces as a thrown axios error — the UI never offers a
 * non-owned brandId, so 403 is an exceptional/forged path, not a normal flow.
 */
import { api } from './api.js';
import type { ApiOk } from './uiSystem.service.js';
import type { ActiveBrand } from './uiSystem.service.js';

/** A brand the user owns (BrandSelector option). */
export interface OwnedBrand {
  brandId: number;
  slug: string;
  name: string;
  /** True when themed by an active design system (selecting it changes the chrome). */
  themed: boolean;
}

export const activeBrandService = {
  /**
   * Resolve the user's active brand (optionally a specific owned `brandId`).
   * Returns `null` on HTTP 204 (no themeable brand → default tokens).
   */
  async getActiveBrand(brandId?: number): Promise<ActiveBrand | null> {
    const res = await api.get<ApiOk<ActiveBrand>>('/entity/active-brand', {
      params: brandId !== undefined ? { brandId } : {},
      // 204 has no body; resolve it as "no active brand" rather than parsing data.
      validateStatus: (s) => s === 200 || s === 204,
    });
    if (res.status === 204) return null;
    return res.data.data;
  },

  /** List the brands the user owns (for the header selector). */
  async listOwnedBrands(): Promise<OwnedBrand[]> {
    const res = await api.get<ApiOk<OwnedBrand[]>>('/entity/active-brand/owned');
    return res.data.data;
  },
};
